/**
 * Asset Disposal Dialog Component
 * 
 * P3 - Fixed Asset Disposal Flow
 * 
 * Allows users to match a bank feed inflow transaction to an existing
 * fixed asset that is being sold/disposed of.
 * 
 * Features:
 * - Lists active fixed assets with book values
 * - Calculates gain/loss on disposal
 * - Posts proper journal entries (remove asset, accumulated depreciation, record gain/loss)
 * - Updates asset status to "disposed"
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Card, CardContent } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import {
  Loader2,
  Package,
  Calendar,
  CheckCircle2,
  AlertCircle,
  ArrowDownRight,
  Sparkles,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Building2,
  Car,
  Monitor,
  Printer,
  Armchair
} from 'lucide-react';

// Asset category icons
const CATEGORY_ICONS = {
  computer_equipment: Monitor,
  office_equipment: Printer,
  furniture: Armchair,
  vehicles: Car,
  machinery: Package,
  buildings: Building2
};

// Category labels
const CATEGORY_LABELS = {
  computer_equipment: 'Computer Equipment',
  office_equipment: 'Office Equipment',
  furniture: 'Furniture & Fixtures',
  vehicles: 'Motor Vehicles',
  machinery: 'Plant & Machinery',
  buildings: 'Buildings',
  equipment: 'Equipment'
};

// Disposal reasons
const DISPOSAL_REASONS = [
  { value: 'sold', label: 'Sold' },
  { value: 'scrapped', label: 'Scrapped / Written Off' },
  { value: 'traded_in', label: 'Traded In' },
  { value: 'donated', label: 'Donated' },
  { value: 'lost', label: 'Lost / Stolen' },
  { value: 'other', label: 'Other' }
];

export const AssetDisposalDialog = ({
  open,
  onOpenChange,
  bankTransaction,
  formatAmount,
  onMatchComplete
}) => {
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [matchData, setMatchData] = useState(null);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [disposalReason, setDisposalReason] = useState('sold');

  // Fetch potential matches when dialog opens
  useEffect(() => {
    if (open && bankTransaction?.id) {
      fetchPotentialMatches();
    } else {
      setMatchData(null);
      setSelectedAssetId(null);
      setDisposalReason('sold');
    }
  }, [open, bankTransaction?.id]);

  const fetchPotentialMatches = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/bank-feeds/asset-disposal-matches/${bankTransaction.id}`);
      setMatchData(response.data);
      
      // Auto-select best match if confidence is high
      if (response.data?.best_match?.confidence_score >= 50) {
        setSelectedAssetId(response.data.best_match.asset_id);
      }
    } catch (error) {
      console.error('Error fetching asset disposal matches:', error);
      toast.error('Failed to load potential asset matches');
    } finally {
      setLoading(false);
    }
  };

  const handleDisposeAsset = async () => {
    if (!selectedAssetId) {
      toast.error('Please select an asset to dispose');
      return;
    }

    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('asset_id', selectedAssetId);
      formData.append('disposal_reason', disposalReason);

      const response = await axios.post(
        `${API}/bank-feeds/review/${bankTransaction.id}/asset-disposal`,
        formData);

      const result = response.data;
      const gainLossText = result.gain_loss >= 0 
        ? `Gain: ${formatAmount?.(result.gain_loss) || result.gain_loss.toLocaleString()}`
        : `Loss: ${formatAmount?.(Math.abs(result.gain_loss)) || Math.abs(result.gain_loss).toLocaleString()}`;
      
      toast.success(`Asset disposed. ${gainLossText}`);
      onMatchComplete?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error disposing asset:', error);
      toast.error(error.response?.data?.detail || 'Failed to dispose asset');
    } finally {
      setProcessing(false);
    }
  };

  const handleNotAssetDisposal = async () => {
    toast.info('Please reclassify this transaction');
    onOpenChange(false);
  };

  const getCategoryIcon = (category) => {
    const Icon = CATEGORY_ICONS[category] || Package;
    return Icon;
  };

  const transactionAmount = Math.abs(bankTransaction?.amount || 0);
  const selectedAsset = matchData?.matching_assets?.find(a => a.asset_id === selectedAssetId);
  const potentialGainLoss = selectedAsset ? transactionAmount - selectedAsset.book_value : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-amber-600" />
            Asset Disposal / Sale
          </DialogTitle>
          <DialogDescription>
            This inflow transaction may be proceeds from an asset sale. Match it to an existing asset to record the disposal.
          </DialogDescription>
        </DialogHeader>

        {/* Bank Transaction Summary */}
        {bankTransaction && (
          <div className="p-4 bg-muted/50 rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center bg-green-100">
                  <ArrowDownRight className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">{bankTransaction.description?.slice(0, 60)}...</p>
                  <p className="text-sm text-muted-foreground">{bankTransaction.date}</p>
                </div>
              </div>
              <span className="text-lg font-semibold text-green-600">
                +{formatAmount?.(transactionAmount) || transactionAmount?.toLocaleString()}
              </span>
            </div>
          </div>
        )}

        <Separator />

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
            <span className="ml-3 text-muted-foreground">Finding matching assets...</span>
          </div>
        )}

        {/* Match Results */}
        {!loading && matchData && (
          <div className="flex-1 overflow-hidden">
            {/* Best Match Suggestion */}
            {matchData.best_match && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-medium">Most Likely Asset</span>
                  <Badge variant="outline" className={
                    matchData.best_match.confidence_score >= 60 ? 'bg-green-50 text-green-700 border-green-200' :
                    matchData.best_match.confidence_score >= 40 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                    'bg-gray-50 text-gray-700 border-gray-200'
                  }>
                    {matchData.best_match.confidence_score}% confidence
                  </Badge>
                </div>
                <Card 
                  className={`cursor-pointer transition-all ${
                    selectedAssetId === matchData.best_match.asset_id 
                      ? 'ring-2 ring-amber-500 bg-amber-50/50' 
                      : 'hover:border-amber-300'
                  }`}
                  onClick={() => setSelectedAssetId(matchData.best_match.asset_id)}
                  data-testid="suggested-asset-match-card"
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          {(() => {
                            const Icon = getCategoryIcon(matchData.best_match.category);
                            return <Icon className="w-4 h-4 text-amber-600" />;
                          })()}
                          <span className="font-medium">{matchData.best_match.asset_name}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline">
                            {CATEGORY_LABELS[matchData.best_match.category] || matchData.best_match.category}
                          </Badge>
                          {matchData.best_match.purchase_date && (
                            <Badge variant="outline" className="text-xs">
                              <Calendar className="w-3 h-3 mr-1" />
                              {matchData.best_match.purchase_date}
                            </Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-3 gap-4 mt-2 text-xs text-muted-foreground">
                          <span>Cost: {formatAmount?.(matchData.best_match.original_cost) || matchData.best_match.original_cost?.toLocaleString()}</span>
                          <span>Dep: {formatAmount?.(matchData.best_match.accumulated_depreciation) || matchData.best_match.accumulated_depreciation?.toLocaleString()}</span>
                          <span className="font-medium">NBV: {formatAmount?.(matchData.best_match.book_value) || matchData.best_match.book_value?.toLocaleString()}</span>
                        </div>
                        {/* Match Reasons */}
                        {matchData.best_match.match_reasons?.length > 0 && (
                          <div className="mt-2 text-xs text-muted-foreground">
                            <span className="font-medium">Why: </span>
                            {matchData.best_match.match_reasons.slice(0, 2).join(', ')}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className={`flex items-center gap-1 text-sm font-medium ${
                          matchData.best_match.potential_gain_loss >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {matchData.best_match.potential_gain_loss >= 0 ? (
                            <TrendingUp className="w-4 h-4" />
                          ) : (
                            <TrendingDown className="w-4 h-4" />
                          )}
                          {matchData.best_match.potential_gain_loss >= 0 ? 'Gain' : 'Loss'}: {
                            formatAmount?.(Math.abs(matchData.best_match.potential_gain_loss)) || 
                            Math.abs(matchData.best_match.potential_gain_loss)?.toLocaleString()
                          }
                        </div>
                        <p className="text-xs text-muted-foreground">on disposal</p>
                        {selectedAssetId === matchData.best_match.asset_id && (
                          <CheckCircle2 className="w-5 h-5 text-amber-600 mt-2 ml-auto" />
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Other Assets */}
            {matchData.matching_assets && matchData.matching_assets.length > 0 && (
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  {matchData.best_match ? 'Other Active Assets' : 'Active Assets'}
                </Label>
                <ScrollArea className="h-[180px] pr-4">
                  <RadioGroup 
                    value={selectedAssetId || ''} 
                    onValueChange={setSelectedAssetId}
                    className="space-y-2"
                  >
                    {matchData.matching_assets
                      .filter(asset => asset.asset_id !== matchData.best_match?.asset_id)
                      .map((asset) => {
                        const Icon = getCategoryIcon(asset.category);
                        const gainLoss = transactionAmount - asset.book_value;
                        return (
                          <div
                            key={asset.asset_id}
                            className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-all ${
                              selectedAssetId === asset.asset_id 
                                ? 'ring-2 ring-amber-500 bg-amber-50/50' 
                                : 'hover:bg-muted/50'
                            }`}
                            onClick={() => setSelectedAssetId(asset.asset_id)}
                            data-testid={`asset-option-${asset.asset_id}`}
                          >
                            <RadioGroupItem value={asset.asset_id} id={asset.asset_id} />
                            <Icon className="w-4 h-4 text-muted-foreground" />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm">{asset.asset_name}</span>
                                <Badge variant="outline" className="text-xs">
                                  {CATEGORY_LABELS[asset.category] || asset.category}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                                <span>Cost: {formatAmount?.(asset.original_cost) || asset.original_cost?.toLocaleString()}</span>
                                <span>NBV: {formatAmount?.(asset.book_value) || asset.book_value?.toLocaleString()}</span>
                              </div>
                            </div>
                            <div className={`text-sm font-medium ${gainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {gainLoss >= 0 ? '+' : ''}{formatAmount?.(gainLoss) || gainLoss?.toLocaleString()}
                            </div>
                          </div>
                        );
                      })}
                  </RadioGroup>
                </ScrollArea>
              </div>
            )}

            {/* Disposal Reason & Summary */}
            {selectedAssetId && (
              <div className="mt-4 p-3 bg-slate-50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-medium">Disposal Reason</Label>
                  </div>
                  <Select value={disposalReason} onValueChange={setDisposalReason}>
                    <SelectTrigger className="w-40 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DISPOSAL_REASONS.map((reason) => (
                        <SelectItem key={reason.value} value={reason.value}>
                          {reason.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedAsset && (
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Sale Proceeds:</span>
                      <span className="font-medium text-green-600">+{formatAmount?.(transactionAmount) || transactionAmount?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Net Book Value:</span>
                      <span className="font-medium">{formatAmount?.(selectedAsset.book_value) || selectedAsset.book_value?.toLocaleString()}</span>
                    </div>
                    <div className="col-span-2 flex justify-between pt-2 border-t">
                      <span className="font-medium">{potentialGainLoss >= 0 ? 'Gain on Disposal:' : 'Loss on Disposal:'}</span>
                      <span className={`font-bold ${potentialGainLoss >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {potentialGainLoss >= 0 ? '+' : ''}{formatAmount?.(potentialGainLoss) || potentialGainLoss?.toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* No Assets Found */}
            {(!matchData.matching_assets || matchData.matching_assets.length === 0) && !matchData.best_match && (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <h3 className="font-medium mb-1">No Matching Assets Found</h3>
                <p className="text-sm text-muted-foreground">
                  No active fixed assets match this transaction.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  You may need to reclassify this as another type of income.
                </p>
              </div>
            )}
          </div>
        )}

        <Separator />

        <DialogFooter className="flex justify-between sm:justify-between">
          <Button
            variant="outline"
            onClick={handleNotAssetDisposal}
            disabled={processing}
            className="text-gray-600 hover:text-gray-700 hover:bg-gray-50"
            data-testid="not-asset-disposal-btn"
          >
            Not Asset Sale
          </Button>
          
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDisposeAsset}
              disabled={processing || !selectedAssetId}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="dispose-asset-btn"
            >
              {processing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Record Disposal
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssetDisposalDialog;
